import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Table2, BarChart3, Download, MessageCircle,
  Settings, Upload, Keyboard, Moon, Sun
} from 'lucide-react';

const CommandPalette = ({
  open,
  onClose,
  onNavigate,
  onChatToggle,
  onSettingsOpen,
  onThemeToggle,
  sheetNames = [],
  onSheetChange,
  hasData,
  isDark,
}) => {
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  const commands = useMemo(() => {
    const cmds = [];

    if (hasData) {
      cmds.push(
        { id: 'overview', label: 'Go to Overview', icon: Table2, action: () => onNavigate('overview'), group: 'Navigation' },
        { id: 'dashboard', label: 'Go to Dashboard', icon: BarChart3, action: () => onNavigate('dashboard'), group: 'Navigation' },
        { id: 'export', label: 'Go to Export', icon: Download, action: () => onNavigate('export'), group: 'Navigation' },
        { id: 'chat', label: 'Toggle AI Chat', icon: MessageCircle, action: onChatToggle, group: 'Actions', shortcut: 'Ctrl+/' },
      );

      sheetNames.forEach((name) => {
        cmds.push({
          id: `sheet-${name}`,
          label: `Switch to sheet: ${name}`,
          icon: Table2,
          action: () => onSheetChange(name),
          group: 'Sheets',
        });
      });
    }

    cmds.push(
      { id: 'settings', label: 'Open Settings', icon: Settings, action: onSettingsOpen, group: 'Actions' },
      { id: 'theme', label: isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode', icon: isDark ? Sun : Moon, action: onThemeToggle, group: 'Actions' },
      { id: 'shortcuts', label: 'Show Keyboard Shortcuts', icon: Keyboard, action: () => {}, group: 'Help', shortcut: '?' },
    );

    return cmds;
  }, [hasData, sheetNames, isDark, onNavigate, onChatToggle, onSettingsOpen, onSheetChange, onThemeToggle]);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(
      (c) => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q)
    );
  }, [commands, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  const executeCommand = (cmd) => {
    cmd.action();
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIdx]) {
      e.preventDefault();
      executeCommand(filtered[selectedIdx]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!open) return null;

  // Group filtered commands
  const groups = {};
  filtered.forEach((cmd) => {
    if (!groups[cmd.group]) groups[cmd.group] = [];
    groups[cmd.group].push(cmd);
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/40"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="w-full max-w-lg bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a command or search..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground bg-muted rounded">
              Esc
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[300px] overflow-y-auto py-1">
            {filtered.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">No results found.</p>
            )}
            {Object.entries(groups).map(([group, cmds]) => (
              <div key={group}>
                <p className="px-4 py-1.5 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{group}</p>
                {cmds.map((cmd) => {
                  const globalIdx = filtered.indexOf(cmd);
                  const isSelected = globalIdx === selectedIdx;
                  return (
                    <button
                      key={cmd.id}
                      onClick={() => executeCommand(cmd)}
                      onMouseEnter={() => setSelectedIdx(globalIdx)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors ${
                        isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      <cmd.icon className="w-4 h-4 shrink-0 opacity-60" />
                      <span className="flex-1 truncate">{cmd.label}</span>
                      {cmd.shortcut && (
                        <kbd className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {cmd.shortcut}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center gap-4 px-4 py-2 border-t text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted rounded font-mono">↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted rounded font-mono">Enter</kbd> Execute</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-muted rounded font-mono">Esc</kbd> Close</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommandPalette;
